import "core-js/es/symbol/iterator"
import "../scss/app.css"

import { createApp, defineAsyncComponent, h } from "vue"
import Vue3Geolocation from "vue3-geolocation"
import VueLazyLoad from "vue3-lazyload"
import http from "./plugins/http"
import events from "./helpers/events"
import piniaPluginPersistedstate from "pinia-plugin-persistedstate"

import RootMixin from "./mixins/root"
import * as Sentry from "@sentry/vue"
import { installNetworkRetry, isChunkLoadError, isNetworkError, isPageInactive, reloadAfterChunkError } from "./helpers/network"

import "@splidejs/vue-splide/css"

/**
 * Custom theme components
 */

import HeroContent from "./components/HeroContent.vue"
import ScrollToTop from "./components/ScrollToTop.vue"
import ScrollHero from "./components/ScrollHero.vue"
import Alert from "./components/alert/Alert.vue"
import SharePage from "./components/SharePage.vue"
import Tabs from "./components/Tabs.vue"
import Accordion from "./components/Accordion.vue"
import AccordionItem from "./components/AccordionItem.vue"
import Reviews from "./components/Reviews.vue"
import ReviewsItem from "./components/ReviewsItem.vue"
import ReviewsGallery from "./components/ReviewsGallery.vue"
import Rating from "./components/Rating.vue"
import ExperienceGallery from "./components/ExperienceGallery.vue"
import ExperienceImageGallery from "./components/ExperienceImageGallery.vue"
import ExperienceIcon from "./components/ExperienceIcon.vue"
import HeroGallery from "./components/HeroGallery.vue"
import HeroTextCarousel from "./components/HeroTextCarousel.vue"
import Description from "./components/Description.vue"
import NewsCarousel from "./components/NewsCarousel.vue"
import LatestNewsGrid from "./components/LatestNewsGrid.vue"
import RelatedBlocks from "./components/related/RelatedBlocks.vue"
import StarCircle from "./components/StarCircle.vue"
import FullWidthImageCarousel from "./components/FullWidthImageCarousel.vue"
import AccordionDynamic from "./components/AccordionDynamic.vue"
import BirdsArchiveGrid from "./components/BirdsArchiveGrid.vue"
import DirectionsButton from "./components/DirectionsButton.vue"
import DistanceFrom from "./components/DistanceFrom.vue"
import ExperienceStatusWeather from "./components/ExperienceStatusWeather.vue"
import ExperienceCheckoutWidget from "./checkout/ExperienceCheckoutWidget.vue"
import HeaderCartWidget from "./checkout/components/HeaderCartWidget.vue"
import LoadError from "./checkout/components/LoadError.vue"
import { createPinia } from "pinia"
import { VueReCaptcha } from "vue-recaptcha-v3"

/**
 * The booking, cart, checkout and thanks pages each have their own code, which only downloads on
 * those pages. While it loads, show what the page put inside the tag ("Loading booking form...").
 * If it can't download, say so and offer to try again rather than leave a gap
 */
const ShowSlot = (props, { slots }) => slots.default?.()
const LoadFailed = () => h(LoadError, {
  class: "py-12",
  message: "Sorry, this part of the page didn't load. Check your connection and try again.",
  onRetry: reloadAfterChunkError,
})
const bookingPage = (loader) => defineAsyncComponent({ loader, loadingComponent: ShowSlot, errorComponent: LoadFailed, delay: 0 })

const BookWidget = bookingPage(() => import("./widget/BookWidget.vue"))
const BookTickets = bookingPage(() => import("./checkout/BookTickets.vue"))
const BookingCartPage = bookingPage(() => import("./checkout/BookingCartPage.vue"))
const BookingCheckoutPage = bookingPage(() => import("./checkout/BookingCheckoutPage.vue"))
const BookingThanksPage = bookingPage(() => import("./checkout/BookingThanksPage.vue"))
const BookThanks = bookingPage(() => import("./widget/BookThanks.vue"))

const clickOutsideDirective = {
  beforeMount(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (el === event.target || el.contains(event.target)) {
        return
      }

      if (typeof binding.value === "function") {
        binding.value(event)
      }
    }

    document.addEventListener("click", el.__clickOutsideHandler__)
    document.addEventListener("touchstart", el.__clickOutsideHandler__)
  },
  unmounted(el) {
    document.removeEventListener("click", el.__clickOutsideHandler__)
    document.removeEventListener("touchstart", el.__clickOutsideHandler__)
    delete el.__clickOutsideHandler__
  },
}



const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

installNetworkRetry()

if (document.querySelectorAll("#app").length > 0) {
  const app = createApp({
    components: {
      Alert,
      SharePage,
      Tabs,
      Accordion,
      AccordionItem,
      Reviews,
      ReviewsGallery,
      ReviewsItem,
      Rating,
      ExperienceGallery,
      ExperienceImageGallery,
      ExperienceIcon,
      HeroContent,
      HeroGallery,
      HeroTextCarousel,

      ScrollToTop,
      ScrollHero,

      Description,
      NewsCarousel,
      LatestNewsGrid,

      RelatedBlocks,

      FullWidthImageCarousel,
      StarCircle,

      DirectionsButton,
      DistanceFrom,

      ExperienceStatusWeather,

      BookWidget,
      ExperienceCheckoutWidget,
      BookTickets,
      BookingCartPage,
      BookingCheckoutPage,
      BookingThanksPage,
      HeaderCartWidget,
      BookThanks,

      AccordionDynamic,
      BirdsArchiveGrid,
    },
    mixins: [RootMixin],
    data: () => ({
      loading: false,
    }),
  })
    .use(Vue3Geolocation)
    .use(VueLazyLoad)
    .use(pinia)
    .use(VueReCaptcha, {
      siteKey: window.recaptchaSiteKey,
      loaderOptions: {
        autoHideBadge: true,
      },
    })
    .directive("on-click-outside", clickOutsideDirective)
    .directive("fixed-width-child", {
      mounted(el) {
        if (el.children[0]) {
          el.children[0].style.width = `${el.clientWidth}px`
        }
      },
    })

  app.config.globalProperties.$events = events
  app.config.globalProperties.$settings = window.storage.settings
  app.config.globalProperties.$http = http

  if (window.capes?.sentry) {
      Sentry.init({
          app,
          dsn: window.capes.sentry,
          // Setting this option to true will send default PII data to Sentry.
          // For example, automatic IP address collection on events
          sendDefaultPii: true,
          // Session Replay is added once the page has loaded, below
          integrations: [Sentry.browserTracingIntegration()],
          // Session Replay
          replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
          replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
          //
          tracesSampleRate: 0.1,

          tracePropagationTargets: [
            "localhost",
            "https://capesfoundation.org.au",
            "https://tr.capesfoundation.org.au"
          ],

          // Facebook/Instagram in-app browser (Android) injects iabjs:// scripts that
          // throw on beforeunload once the native bridge is torn down. Not our code.
          denyUrls: [/^iabjs:\/\//],
          ignoreErrors: [/Java object is gone/],

          // Requests and downloads cut off by the user leaving the page aren't real failures
          beforeSend(event, hint) {
            const error = hint?.originalException

            if ((isNetworkError(error) || isChunkLoadError(error)) && isPageInactive()) {
              return null
            }

            return event
          },
      })

      // Session Replay is a big download and starts recording the page straight away, so leave it
      // until the page has loaded. Errors before then won't have a replay
      const addReplay = () => {
        import("./helpers/sentry-replay")
          .then(({ replayIntegration }) => {
            Sentry.addIntegration(replayIntegration())
          })
          // Replay is optional, so a failed download isn't worth an error report
          .catch(() => {})
      }

      if (document.readyState === "complete") {
        addReplay()
      } else {
        window.addEventListener("load", addReplay, { once: true })
      }
  }


  // Mounting rebuilds everything inside #app, so the hero video would start again from the
  // beginning about a second after it starts playing, and the rebuilt one downloads the file a
  // second time. Put the original, still-playing one back, and empty the rebuilt one before it
  // starts loading
  const heroVideo = document.querySelector("#app video[data-hero-video]")

  app.mount("#app")

  const rebuiltHeroVideo = document.querySelector("#app video[data-hero-video]")
  if (heroVideo && rebuiltHeroVideo && rebuiltHeroVideo !== heroVideo) {
    rebuiltHeroVideo.replaceWith(heroVideo)
    rebuiltHeroVideo.removeAttribute("autoplay")
    rebuiltHeroVideo.replaceChildren()
    rebuiltHeroVideo.load()
  }
}
